import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, CATEGORY_LABELS } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Trash2, Rocket, ShieldCheck, Shield, Crown, Users as UsersIcon, MapPin, CreditCard, BarChart3, ExternalLink, RefreshCw, Star } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [places, setPlaces] = useState([]);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAll = async () => {
    setRefreshing(true);
    try {
      const [s, p, u, t] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/places"),
        api.get("/admin/users"),
        api.get("/admin/transactions"),
      ]);
      setStats(s.data); setPlaces(p.data); setUsers(u.data); setTransactions(t.data);
    } catch (err) {
      toast.error("Erreur de chargement");
    } finally { setRefreshing(false); }
  };

  useEffect(() => { if (user?.is_admin) fetchAll(); }, [user]);

  if (loading) return <div className="pt-32 text-center">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <NotAuthorized />;

  const deletePlace = async (id, name) => {
    if (!window.confirm(`Supprimer "${name}" ?`)) return;
    await api.delete(`/admin/places/${id}`);
    toast.success("Supprimé");
    fetchAll();
  };

  const boost = async (id) => {
    await api.post(`/admin/places/${id}/boost?days=30`);
    toast.success("Boosté 30 jours");
    fetchAll();
  };

  const unboost = async (id) => {
    await api.post(`/admin/places/${id}/unboost`);
    toast.success("Boost retiré");
    fetchAll();
  };

  const setRole = async (id, field, value) => {
    await api.put(`/admin/users/${id}/role?${field}=${value}`);
    toast.success("Rôle mis à jour");
    fetchAll();
  };

  const deleteUser = async (id, email) => {
    if (!window.confirm(`Supprimer ${email} et ses lieux ?`)) return;
    await api.delete(`/admin/users/${id}`);
    toast.success("Supprimé");
    fetchAll();
  };

  return (
    <div className="min-h-screen bg-[#FAF1E1]" data-testid="admin-page">
      <div className="pt-24 sm:pt-28 pb-16 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="uppercase-label text-[#FF6B35] flex items-center gap-1.5"><Crown size={11}/> Administration</div>
            <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tighter text-[#1A1A1A] mt-1">
              Panneau Admin
            </h1>
            <p className="text-[#1A1A1A]/60 mt-2">Gestion globale de RunSpot.</p>
          </div>
          <Button onClick={fetchAll} disabled={refreshing} data-testid="refresh-btn"
            variant="outline" className="rounded-full">
            <RefreshCw size={14} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`}/> Rafraîchir
          </Button>
        </div>

        <Tabs defaultValue="stats" className="w-full" data-testid="admin-tabs">
          <TabsList className="rounded-full bg-white border border-black/5 shadow-sm p-1 h-auto flex-wrap">
            <TabsTrigger value="stats" data-testid="tab-stats" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white px-4 py-2 font-bold">
              <BarChart3 size={14} className="mr-1.5"/> Stats
            </TabsTrigger>
            <TabsTrigger value="places" data-testid="tab-places" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white px-4 py-2 font-bold">
              <MapPin size={14} className="mr-1.5"/> Spots ({places.length})
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white px-4 py-2 font-bold">
              <UsersIcon size={14} className="mr-1.5"/> Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white px-4 py-2 font-bold">
              <CreditCard size={14} className="mr-1.5"/> Paiements ({transactions.length})
            </TabsTrigger>
          </TabsList>

          {/* STATS */}
          <TabsContent value="stats" className="mt-6">
            {stats && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                <Stat label="Utilisateurs" value={stats.users} accent="#00A6B3" testid="stat-users"/>
                <Stat label="Commerçants" value={stats.merchants} accent="#2A9D8F" testid="stat-merchants"/>
                <Stat label="Spots" value={stats.places} accent="#FF6B35" testid="stat-places"/>
                <Stat label="Boostés" value={stats.promoted} accent="#F4A261" testid="stat-promoted"/>
                <Stat label="Événements" value={stats.events} accent="#6C2BD9" testid="stat-events"/>
                <Stat label="Transactions payées" value={stats.paid_transactions} accent="#E91E63" testid="stat-paid-tx"/>
                <Stat label="Revenu total" value={`${stats.revenue_eur.toFixed(2)} €`} accent="#1A1A1A" testid="stat-revenue" big/>
              </div>
            )}
            {stats && (
              <div className="mt-6 rounded-3xl bg-white border border-black/5 p-6">
                <div className="uppercase-label text-[#1A1A1A]/60">Répartition par catégorie</div>
                <div className="mt-4 space-y-3">
                  {Object.entries(stats.by_category).map(([cat, count]) => {
                    const max = Math.max(...Object.values(stats.by_category));
                    const pct = Math.round((count / max) * 100);
                    return (
                      <div key={cat}>
                        <div className="flex justify-between text-sm font-bold mb-1">
                          <span>{CATEGORY_LABELS[cat] || cat}</span>
                          <span className="text-[#FF6B35]">{count}</span>
                        </div>
                        <div className="h-2 bg-[#FAF1E1] rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-[#FF6B35] to-[#E84A1F]" style={{ width: `${pct}%` }}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </TabsContent>

          {/* PLACES */}
          <TabsContent value="places" className="mt-6">
            <div className="rounded-3xl bg-white border border-black/5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF1E1] text-left">
                  <tr>
                    <th className="p-3 font-bold">Nom</th>
                    <th className="p-3 font-bold">Catégorie</th>
                    <th className="p-3 font-bold">Ville</th>
                    <th className="p-3 font-bold">Note</th>
                    <th className="p-3 font-bold">Boost</th>
                    <th className="p-3 font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {places.map(p => (
                    <tr key={p.id} className="border-t border-black/5 hover:bg-[#FAF1E1]/40" data-testid={`admin-place-${p.id}`}>
                      <td className="p-3 font-semibold">
                        <Link to={`/place/${p.id}`} className="hover:text-[#FF6B35] hover:underline">{p.name}</Link>
                      </td>
                      <td className="p-3 text-[#1A1A1A]/70">{CATEGORY_LABELS[p.category] || p.category}{p.subcategory ? ` · ${p.subcategory}` : ""}</td>
                      <td className="p-3 text-[#1A1A1A]/70">{p.city}</td>
                      <td className="p-3"><Star size={12} className="inline fill-[#FF6B35] text-[#FF6B35] mr-1"/>{p.rating?.toFixed(1)}</td>
                      <td className="p-3">{p.is_promoted ? <Badge className="bg-[#FF6B35]">Boosté</Badge> : <span className="text-[#1A1A1A]/40 text-xs">—</span>}</td>
                      <td className="p-3 flex gap-1">
                        {p.is_promoted ? (
                          <Button size="sm" variant="outline" onClick={()=>unboost(p.id)} data-testid={`unboost-${p.id}`} className="rounded-full text-xs">Retirer</Button>
                        ) : (
                          <Button size="sm" onClick={()=>boost(p.id)} data-testid={`boost-${p.id}`} className="rounded-full text-xs bg-[#FF6B35] hover:bg-[#E84A1F]">
                            <Rocket size={12} className="mr-1"/> Boost
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={()=>deletePlace(p.id, p.name)} data-testid={`del-place-${p.id}`} className="rounded-full text-xs text-red-600"><Trash2 size={12}/></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* USERS */}
          <TabsContent value="users" className="mt-6">
            <div className="rounded-3xl bg-white border border-black/5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF1E1] text-left">
                  <tr>
                    <th className="p-3 font-bold">Email</th>
                    <th className="p-3 font-bold">Nom</th>
                    <th className="p-3 font-bold">Rôles</th>
                    <th className="p-3 font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-t border-black/5 hover:bg-[#FAF1E1]/40" data-testid={`admin-user-${u.id}`}>
                      <td className="p-3 font-semibold">{u.email}{u.id === user.id && <span className="text-xs text-[#FF6B35] ml-2">(toi)</span>}</td>
                      <td className="p-3 text-[#1A1A1A]/70">{u.name}</td>
                      <td className="p-3 flex gap-1">
                        {u.is_admin && <Badge className="bg-[#FF6B35]"><Crown size={10} className="mr-1"/>Admin</Badge>}
                        {u.is_merchant && <Badge variant="outline">Commerçant</Badge>}
                        {!u.is_merchant && !u.is_admin && <Badge variant="outline">Utilisateur</Badge>}
                      </td>
                      <td className="p-3 flex gap-1 flex-wrap">
                        <Button size="sm" variant="outline" onClick={()=>setRole(u.id, "is_merchant", !u.is_merchant)} data-testid={`role-merch-${u.id}`} className="rounded-full text-xs">
                          {u.is_merchant ? "Retirer commerçant" : "→ Commerçant"}
                        </Button>
                        <Button size="sm" variant="outline" onClick={()=>setRole(u.id, "is_admin", !u.is_admin)} data-testid={`role-admin-${u.id}`} className="rounded-full text-xs">
                          {u.is_admin ? <><Shield size={12} className="mr-1"/>Retirer admin</> : <><ShieldCheck size={12} className="mr-1"/>→ Admin</>}
                        </Button>
                        {u.id !== user.id && (
                          <Button size="sm" variant="outline" onClick={()=>deleteUser(u.id, u.email)} data-testid={`del-user-${u.id}`} className="rounded-full text-xs text-red-600"><Trash2 size={12}/></Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* PAYMENTS */}
          <TabsContent value="payments" className="mt-6">
            <div className="rounded-3xl bg-gradient-to-br from-[#FF6B35]/10 via-transparent to-[#00A6B3]/10 border border-[#FF6B35]/20 p-5 mb-4">
              <p className="text-sm text-[#1A1A1A]/80">
                💳 Les paiements transitent par <strong>Stripe</strong>. Pour gérer remboursements, virements ou litiges, connectez-vous à votre <a href="https://dashboard.stripe.com" target="_blank" rel="noreferrer" className="text-[#FF6B35] font-bold hover:underline inline-flex items-center gap-1">Stripe Dashboard <ExternalLink size={11}/></a>
              </p>
            </div>
            <div className="rounded-3xl bg-white border border-black/5 overflow-x-auto">
              {transactions.length === 0 ? (
                <div className="p-12 text-center text-[#1A1A1A]/50">Aucune transaction enregistrée.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[#FAF1E1] text-left">
                    <tr>
                      <th className="p-3 font-bold">Date</th>
                      <th className="p-3 font-bold">Commerçant</th>
                      <th className="p-3 font-bold">Spot</th>
                      <th className="p-3 font-bold">Pack</th>
                      <th className="p-3 font-bold">Montant</th>
                      <th className="p-3 font-bold">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(t => (
                      <tr key={t.id} className="border-t border-black/5" data-testid={`tx-${t.id}`}>
                        <td className="p-3 text-xs text-[#1A1A1A]/70">{new Date(t.created_at).toLocaleString("fr-FR")}</td>
                        <td className="p-3 font-semibold">{t.user_email}</td>
                        <td className="p-3 text-[#1A1A1A]/70">{t.place_name}</td>
                        <td className="p-3"><Badge variant="outline">{t.package_id}</Badge></td>
                        <td className="p-3 font-bold">{t.amount?.toFixed(2)} €</td>
                        <td className="p-3">
                          {t.payment_status === "paid" ? <Badge className="bg-[#4CAF50]">Payé</Badge> :
                           t.payment_status === "pending" ? <Badge variant="outline">En attente</Badge> :
                           <Badge variant="outline" className="text-red-600">{t.payment_status}</Badge>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Stat({ label, value, accent, big, testid }) {
  return (
    <div data-testid={testid} className={`rounded-3xl bg-white border border-black/5 p-5 ${big ? "sm:col-span-2" : ""}`}>
      <div className="uppercase-label text-[#1A1A1A]/60">{label}</div>
      <div className="font-display font-black text-3xl sm:text-4xl mt-2" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function NotAuthorized() {
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="text-center">
        <Shield size={48} className="mx-auto text-[#FF6B35] mb-3"/>
        <h1 className="font-display font-black text-2xl">Accès réservé aux admins</h1>
        <p className="text-[#1A1A1A]/60 mt-2">Vous n'avez pas les droits administrateur.</p>
        <Link to="/" className="mt-4 inline-block text-[#FF6B35] font-bold hover:underline">Retour à l'accueil</Link>
      </div>
    </div>
  );
}
